import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiTrash2, FiCheck, FiX, FiArrowUp, FiMessageSquare, FiEye, FiEdit2 } from 'react-icons/fi';

const TABS = ['Workflows', 'My Approvals', 'All Requests', 'Comments'];
const ENTITY_TYPES = ['expense_claim', 'bill', 'leave_request', 'journal', 'invoice', 'quote'];
const emptyStage = { stageOrder: 1, name: '', approverUserId: '', amountThreshold: '', escalationAfterHours: '' };

const Workflow = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Workflows');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [workflows, setWorkflows] = useState([]);
  const [myApprovals, setMyApprovals] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);

  const [workflowForm, setWorkflowForm] = useState({ appliesToType: 'expense_claim', name: '', stages: [{ ...emptyStage }] });
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [stages, setStages] = useState(null);

  const [editingStageId, setEditingStageId] = useState(null);
  const [editStageForm, setEditStageForm] = useState(null);

  const [decisionComments, setDecisionComments] = useState({});

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [decisions, setDecisions] = useState(null);

  const [commentEntityType, setCommentEntityType] = useState('expense_claim');
  const [commentEntityId, setCommentEntityId] = useState('');
  const [comments, setComments] = useState(null);
  const [newComment, setNewComment] = useState('');

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/workflow/workflows'),
      apiClient.get('/workflow/approvals/me'),
      apiClient.get('/workflow/approvals').catch(() => ({ data: { data: [] } })),
      apiClient.get('/organisations/current/users').catch(() => ({ data: { data: [] } })),
    ]).then(([wfRes, mineRes, allRes, usersRes]) => {
      setWorkflows(wfRes.data.data);
      setMyApprovals(mineRes.data.data);
      setAllRequests(allRes.data.data);
      setOrgUsers(usersRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load workflow data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const workflowName = (id) => workflows.find((w) => w.id === id)?.name || id;
  const userName = (id) => {
    const u = orgUsers.find((x) => x.userId === id);
    return u ? `${u.firstName} ${u.lastName}` : id;
  };

  // ---- Workflows ----
  const updateStage = (idx, field, value) => {
    setWorkflowForm((prev) => {
      const stagesArr = [...prev.stages];
      stagesArr[idx] = { ...stagesArr[idx], [field]: value };
      return { ...prev, stages: stagesArr };
    });
  };
  const addStage = () => setWorkflowForm((prev) => ({ ...prev, stages: [...prev.stages, { ...emptyStage, stageOrder: prev.stages.length + 1 }] }));
  const removeStage = (idx) => setWorkflowForm((prev) => ({ ...prev, stages: prev.stages.filter((_, i) => i !== idx) }));

  const handleCreateWorkflow = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...workflowForm,
        stages: workflowForm.stages.map((s) => ({
          stageOrder: Number(s.stageOrder),
          name: s.name,
          ...(s.approverUserId ? { approverUserId: s.approverUserId } : {}),
          ...(s.amountThreshold ? { amountThreshold: s.amountThreshold } : {}),
          ...(s.escalationAfterHours ? { escalationAfterHours: Number(s.escalationAfterHours) } : {}),
        })),
      };
      await apiClient.post('/workflow/workflows', payload);
      success('✅ Workflow created');
      setShowForm(false);
      setWorkflowForm({ appliesToType: 'expense_claim', name: '', stages: [{ ...emptyStage }] });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create workflow')); }
  };

  const viewWorkflow = async (wf) => {
    setSelectedWorkflow(wf);
    setStages(null);
    setEditingStageId(null);
    try {
      const { data } = await apiClient.get(`/workflow/workflows/${wf.id}/stages`);
      setStages(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load stages')); }
  };

  const handleToggleWorkflowActive = async (workflowId, currentlyActive) => {
    try {
      await apiClient.patch(`/workflow/workflows/${workflowId}`, { isActive: !currentlyActive });
      success(currentlyActive ? '✅ Workflow deactivated' : '✅ Workflow reactivated');
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update workflow')); }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (!window.confirm('Delete this workflow permanently? Only works if it has never been used.')) return;
    try {
      await apiClient.delete(`/workflow/workflows/${workflowId}`);
      success('✅ Workflow deleted');
      if (selectedWorkflow?.id === workflowId) setSelectedWorkflow(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete workflow')); }
  };

  const startEditStage = (stage) => {
    setEditingStageId(stage.id);
    setEditStageForm({
      name: stage.name,
      approverUserId: stage.approverUserId || '',
      amountThreshold: stage.amountThreshold || '',
      escalationAfterHours: stage.escalationAfterHours || '',
    });
  };

  const handleSaveStage = async (e, stageId) => {
    e.preventDefault();
    try {
      const payload = { ...editStageForm };
      if (!payload.approverUserId) delete payload.approverUserId;
      if (!payload.amountThreshold) delete payload.amountThreshold;
      if (!payload.escalationAfterHours) delete payload.escalationAfterHours;
      else payload.escalationAfterHours = Number(payload.escalationAfterHours);
      await apiClient.patch(`/workflow/workflows/${selectedWorkflow.id}/stages/${stageId}`, payload);
      success('✅ Stage updated');
      setEditingStageId(null);
      const { data } = await apiClient.get(`/workflow/workflows/${selectedWorkflow.id}/stages`);
      setStages(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not update stage')); }
  };

  // ---- Approvals ----
  const handleDecide = async (requestId, decision) => {
    try {
      await apiClient.post(`/workflow/approvals/${requestId}/decide`, {
        decision,
        comments: decisionComments[requestId] || undefined,
      });
      success(`✅ Request ${decision}`);
      setDecisionComments((p) => ({ ...p, [requestId]: '' }));
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not record decision')); }
  };

  const viewRequest = async (req) => {
    setSelectedRequest(req);
    setDecisions(null);
    try {
      const { data } = await apiClient.get(`/workflow/approvals/${req.id}/decisions`);
      setDecisions(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load decisions')); }
  };

  // ---- Comments ----
  const loadComments = async () => {
    if (!commentEntityId) { showError('Enter an entity ID first'); return; }
    try {
      const { data } = await apiClient.get('/comments', { params: { entityType: commentEntityType, entityId: commentEntityId } });
      setComments(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load comments')); }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await apiClient.post('/comments', { entityType: commentEntityType, entityId: commentEntityId, body: newComment });
      setNewComment('');
      loadComments();
    } catch (err) { showError(getErrorMessage(err, 'Could not add comment')); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await apiClient.delete(`/comments/${commentId}`);
      loadComments();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete comment')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Workflow</h1>
        <p>Approval routing and comments on any record</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); setSelectedWorkflow(null); setSelectedRequest(null); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Workflows' && (
        <>
          <section className="page-section">
            <div className="section-header">
              <h2>Workflow Templates</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiPlus size={14} /> {showForm ? 'Cancel' : 'New Workflow'}
              </button>
            </div>
            {showForm && (
              <form onSubmit={handleCreateWorkflow} style={{ marginBottom: '1.5rem' }}>
                <div className="form-grid">
                  <div className="form-field"><label>Name *</label><input required value={workflowForm.name} onChange={(e) => setWorkflowForm(p => ({ ...p, name: e.target.value }))} placeholder="Expense Approval" /></div>
                  <div className="form-field">
                    <label>Applies To *</label>
                    <select required value={workflowForm.appliesToType} onChange={(e) => setWorkflowForm(p => ({ ...p, appliesToType: e.target.value }))}>
                      {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>Stages (in order)</label>
                  {workflowForm.stages.map((s, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                      <span style={{ width: '24px', fontWeight: 700, color: '#6b7280' }}>{idx + 1}</span>
                      <input required placeholder="Stage name" value={s.name} onChange={(e) => updateStage(idx, 'name', e.target.value)} style={{ flex: 1, padding: '0.5rem' }} />
                      <select value={s.approverUserId} onChange={(e) => updateStage(idx, 'approverUserId', e.target.value)} style={{ width: '180px', padding: '0.5rem' }}>
                        <option value="">-- approver --</option>
                        {orgUsers.map((u) => <option key={u.userId} value={u.userId}>{u.firstName} {u.lastName}</option>)}
                      </select>
                      <input type="number" step="0.01" placeholder="Min amount" value={s.amountThreshold} onChange={(e) => updateStage(idx, 'amountThreshold', e.target.value)} style={{ width: '110px', padding: '0.5rem' }} />
                      <input type="number" placeholder="Escalate (hrs)" value={s.escalationAfterHours} onChange={(e) => updateStage(idx, 'escalationAfterHours', e.target.value)} style={{ width: '110px', padding: '0.5rem' }} />
                      {workflowForm.stages.length > 1 && (
                        <button type="button" className="action-icon delete" onClick={() => removeStage(idx)}><FiTrash2 size={14} /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={addStage}>
                    <FiPlus size={14} /> Add Stage
                  </button>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create Workflow</button>
                </div>
              </form>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Name</th><th>Applies To</th><th>Active</th><th>Actions</th></tr></thead>
                <tbody>
                  {workflows.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No workflows yet</td></tr>
                  ) : workflows.map((w) => (
                    <tr key={w.id}>
                      <td style={{ fontWeight: 600 }}>{w.name}</td>
                      <td style={{ textTransform: 'capitalize' }}>{w.appliesToType?.replace('_', ' ')}</td>
                      <td><span className="badge" style={{ background: w.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{w.isActive ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <div className="actions">
                          <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewWorkflow(w)}>View Stages</button>
                          <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleToggleWorkflowActive(w.id, w.isActive)}>
                            {w.isActive ? 'Deactivate' : 'Reactivate'}
                          </button>
                          <button className="action-icon delete" title="Delete" onClick={() => handleDeleteWorkflow(w.id)}>
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {selectedWorkflow && (
            <section className="page-section">
              <div className="section-header">
                <h2>Stages — {selectedWorkflow.name}</h2>
                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedWorkflow(null)}>Close</button>
              </div>
              {stages === null ? <p style={{ color: '#6b7280' }}>Loading...</p> : (
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>#</th><th>Name</th><th>Approver</th><th>Min Amount</th><th>Escalate After</th></tr></thead>
                    <tbody>
                      {stages.map((s) => (
                        <tr key={s.id}>
                          {editingStageId === s.id ? (
                            <td colSpan={5}>
                              <form onSubmit={(e) => handleSaveStage(e, s.id)} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0' }}>
                                <input value={editStageForm.name} onChange={(e) => setEditStageForm(p => ({ ...p, name: e.target.value }))} style={{ flex: 1, padding: '0.4rem' }} />
                                <select value={editStageForm.approverUserId} onChange={(e) => setEditStageForm(p => ({ ...p, approverUserId: e.target.value }))} style={{ padding: '0.4rem' }}>
                                  <option value="">-- approver --</option>
                                  {orgUsers.map((u) => <option key={u.userId} value={u.userId}>{u.firstName} {u.lastName}</option>)}
                                </select>
                                <input type="number" step="0.01" placeholder="Min amount" value={editStageForm.amountThreshold} onChange={(e) => setEditStageForm(p => ({ ...p, amountThreshold: e.target.value }))} style={{ width: '110px', padding: '0.4rem' }} />
                                <input type="number" placeholder="Escalate (hrs)" value={editStageForm.escalationAfterHours} onChange={(e) => setEditStageForm(p => ({ ...p, escalationAfterHours: e.target.value }))} style={{ width: '110px', padding: '0.4rem' }} />
                                <button type="submit" className="btn btn-sm btn-primary" style={{ width: 'auto' }}>Save</button>
                                <button type="button" className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setEditingStageId(null)}>Cancel</button>
                              </form>
                            </td>
                          ) : (
                            <>
                              <td>{s.stageOrder}</td>
                              <td style={{ fontWeight: 600 }}>{s.name}</td>
                              <td>{s.approverUserId ? userName(s.approverUserId) : '—'}</td>
                              <td>{s.amountThreshold ? Number(s.amountThreshold).toFixed(2) : '—'}</td>
                              <td>
                                {s.escalationAfterHours ? `${s.escalationAfterHours}h` : '—'}
                                <button className="action-icon edit" title="Edit" onClick={() => startEditStage(s)} style={{ marginLeft: '0.5rem' }}>
                                  <FiEdit2 size={14} />
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {tab === 'My Approvals' && (
        <section className="page-section">
          <div className="section-header"><h2>Awaiting My Decision</h2></div>
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Workflow</th><th>Entity</th><th>Amount</th><th>Requested</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : myApprovals.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nothing waiting on you right now</td></tr>
                ) : myApprovals.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{workflowName(r.workflowId)}</td>
                    <td style={{ textTransform: 'capitalize' }}>{r.entityType?.replace('_', ' ')} — {r.entityId.slice(0, 8)}</td>
                    <td>{r.amount ? Number(r.amount).toFixed(2) : '—'}</td>
                    <td>{new Date(r.requestedAt).toLocaleDateString()}</td>
                    <td>
                      <div className="actions" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
                        <input
                          placeholder="Comment (optional)"
                          value={decisionComments[r.id] || ''}
                          onChange={(e) => setDecisionComments((p) => ({ ...p, [r.id]: e.target.value }))}
                          style={{ padding: '0.3rem', width: '140px' }}
                        />
                        <button className="action-icon edit" title="Approve" onClick={() => handleDecide(r.id, 'approved')}><FiCheck size={16} /></button>
                        <button className="action-icon delete" title="Reject" onClick={() => handleDecide(r.id, 'rejected')}><FiX size={16} /></button>
                        <button className="action-icon edit" title="Escalate" onClick={() => handleDecide(r.id, 'escalated')}><FiArrowUp size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'All Requests' && (
        <>
          <section className="page-section">
            <div className="section-header"><h2>All Approval Requests</h2></div>
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Workflow</th><th>Entity</th><th>Status</th><th>Requested</th><th>Actions</th></tr></thead>
                <tbody>
                  {allRequests.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No requests yet</td></tr>
                  ) : allRequests.map((r) => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600 }}>{workflowName(r.workflowId)}</td>
                      <td style={{ textTransform: 'capitalize' }}>{r.entityType?.replace('_', ' ')} — {r.entityId.slice(0, 8)}</td>
                      <td>
                        <span className="badge" style={{ background: r.status === 'approved' ? '#10b981' : r.status === 'rejected' ? '#ef4444' : r.status === 'escalated' ? '#f59e0b' : '#3b82f6', color: 'white' }}>{r.status}</span>
                      </td>
                      <td>{new Date(r.requestedAt).toLocaleDateString()}</td>
                      <td><button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewRequest(r)}><FiEye size={14} /> History</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {selectedRequest && (
            <section className="page-section">
              <div className="section-header">
                <h2>Decision History</h2>
                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedRequest(null)}>Close</button>
              </div>
              {decisions === null ? <p style={{ color: '#6b7280' }}>Loading...</p> : decisions.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No decisions recorded yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {decisions.map((d) => (
                    <div key={d.id} style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <strong style={{ textTransform: 'capitalize' }}>{d.decision}</strong>
                        <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>{new Date(d.decidedAt).toLocaleString()}</span>
                      </div>
                      {d.comments && <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.9rem' }}>{d.comments}</p>}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {tab === 'Comments' && (
        <section className="page-section">
          <div className="section-header"><h2>Comments on a Record</h2></div>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Comments attach to any record in the system by type + ID — paste an entity ID from elsewhere (e.g. an invoice or bill row) to view or add notes on it.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <select value={commentEntityType} onChange={(e) => setCommentEntityType(e.target.value)} style={{ padding: '0.5rem' }}>
              {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
            <input placeholder="Entity ID" value={commentEntityId} onChange={(e) => setCommentEntityId(e.target.value)} style={{ flex: 1, padding: '0.5rem' }} />
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={loadComments}><FiMessageSquare size={14} /> Load</button>
          </div>

          {comments !== null && (
            <>
              <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <input placeholder="Add a comment..." value={newComment} onChange={(e) => setNewComment(e.target.value)} style={{ flex: 1, padding: '0.5rem' }} />
                <button type="submit" className="btn btn-sm btn-primary" style={{ width: 'auto' }}>Post</button>
              </form>
              {comments.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No comments on this record yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {comments.map((c) => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                      <div>
                        <p style={{ margin: 0 }}>{c.body}</p>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{new Date(c.createdAt).toLocaleString()}</span>
                      </div>
                      <button className="action-icon delete" title="Delete" onClick={() => handleDeleteComment(c.id)}><FiTrash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}
    </motion.div>
  );
};

export default Workflow;